/**
 * 💕 Friendship Hearts — daily gift-a-friend widget on the /pet page.
 *
 * Mar 2026 owner canon updates:
 *   • Global daily gift-type lock REMOVED — each friend independently pickable
 *   • 💌 Gift-Request Wish added — user posts a wish (e.g. "I want Hearts")
 *     that friends see on their tile & can fulfill in one tap
 *
 * Rules:
 *   • Sender picks 1 of 4 gifts per friend: 💕 Hearts, 🍇 Flux Fruit, ☯️ Soul Key, 💵 OD
 *   • 1 send per friend per PT day · 20 sends/day cap
 *   • Only 💕 Hearts raises the friend's Hearts counter
 *   • Sender pays nothing
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FriendStable() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickerFor, setPickerFor] = useState(null);   // target friend row when picker is open
  const [sending, setSending] = useState(null);       // friend_user_id currently in flight
  const [wishPickerOpen, setWishPickerOpen] = useState(false);
  const [wishSaving, setWishSaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pet/friend-feed-targets`);
      setState(r.data);
    } catch (e) {
      // Silent — widget hides itself if user has no friends
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onSendGift = async (target, giftType) => {
    if (sending || target.already_fed_today) return;
    setSending(target.user_id);
    try {
      const r = await axios.post(`${API}/pet/feed-friend`, {
        friend_user_id: target.user_id,
        gift_type: giftType,
      });
      const msg = r.data.hearts_gained > 0
        ? `${r.data.gift_icon} ${r.data.gift_label} sent to ${target.name}! +${r.data.hearts_gained} Hearts 💕 to their counter.`
        : `${r.data.gift_icon} ${r.data.gift_label} sent to ${target.name}!`;
      toast.success(msg);
      // Optimistic update — mark fed + bump daily counter
      setState((prev) => prev && ({
        ...prev,
        targets: prev.targets.map((t) =>
          t.user_id === target.user_id ? { ...t, already_fed_today: true } : t
        ),
        daily_used: (prev.daily_used || 0) + 1,
      }));
      setPickerFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send friendship gift.");
    } finally {
      setSending(null);
    }
  };

  const onSetWish = async (giftType) => {
    setWishSaving(true);
    try {
      const r = await axios.post(`${API}/pet/gift-wish/set`, { gift_type: giftType });
      setState((prev) => prev && ({ ...prev, my_wish: r.data.wish }));
      toast.success(giftType
        ? `💌 Your wish is now: ${r.data.wish?.icon} ${r.data.wish?.label}`
        : "💌 Wish cleared.");
      setWishPickerOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save wish.");
    } finally {
      setWishSaving(false);
    }
  };

  if (loading) return null;
  if (!state || !(state.targets || []).length) return null;

  const {
    targets, daily_used = 0, daily_cap = 20,
    gift_catalog = [],
    hearts_per_gift = 10, total_hearts = 0,
    my_wish = null,
  } = state;

  return (
    <div className="mb-6" data-testid="friendship-hearts">
      <div className="flex items-end justify-between mb-3">
        <div>
          <h2 className="font-display text-2xl">💕 Friendship Hearts</h2>
          <div className="text-xs text-neutral-500 mt-0.5">
            Send a gift to a friend — pick from{" "}
            <span className="text-[#F472B6] font-bold">💕 Hearts, 🍇 Flux Fruit, ☯️ Soul Key, or 💵 Omni Dollars</span>.
            Once per friend per day. Free.
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold tabular-nums" data-testid="friendship-daily-counter">
            {Math.max(0, daily_cap - daily_used)} / {daily_cap} <span className="text-neutral-600">left today</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#F472B6] font-bold tabular-nums mt-0.5" data-testid="friendship-hearts-total">
            💕 {total_hearts} hearts
          </div>
        </div>
      </div>

      {/* 💌 My Gift Wish — friends see this and can send it */}
      <div
        className="mb-3 flex items-center justify-between gap-3 bg-black border-2 border-[#F472B6]/30 hover:border-[#F472B6]/60 p-2.5 transition cursor-pointer"
        onClick={() => setWishPickerOpen(true)}
        data-testid="my-gift-wish-chip"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-2xl leading-none shrink-0">💌</span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#F472B6] font-bold">Your wish</div>
            {my_wish ? (
              <div className="text-sm font-bold truncate">
                <span className="mr-1">{my_wish.icon}</span>{my_wish.label}
              </div>
            ) : (
              <div className="text-sm text-neutral-500 italic">Tap to post a wish for your friends</div>
            )}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-bold shrink-0">
          {my_wish ? "change" : "pick"} →
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {targets.map((t) => (
          <FriendCard
            key={t.user_id}
            target={t}
            sending={sending === t.user_id}
            onOpenPicker={() => setPickerFor(t)}
            onQuickFulfillWish={() => t.gift_wish && onSendGift(t, t.gift_wish.id)}
          />
        ))}
      </div>

      {/* Gift-picker modal (per friend, no global lock) */}
      {pickerFor && (
        <GiftPicker
          target={pickerFor}
          giftCatalog={gift_catalog}
          onClose={() => setPickerFor(null)}
          onPick={(giftId) => onSendGift(pickerFor, giftId)}
          sending={sending === pickerFor.user_id}
        />
      )}

      {/* 💌 Wish picker modal */}
      {wishPickerOpen && (
        <WishPicker
          giftCatalog={gift_catalog}
          currentWish={my_wish}
          onClose={() => setWishPickerOpen(false)}
          onPick={(giftId) => onSetWish(giftId)}
          onClear={() => onSetWish(null)}
          saving={wishSaving}
        />
      )}
    </div>
  );
}

function FriendCard({ target, sending, onOpenPicker, onQuickFulfillWish }) {
  const fed = target.already_fed_today;
  const pet = target.pet || {};
  const xpPct = Math.min(100, Math.round((pet.xp / Math.max(1, pet.xp_cap)) * 100));
  const handle = target.handle ? `@${target.handle}` : null;
  const wish = target.gift_wish;   // { id, label, icon } or null
  return (
    <div
      data-testid={`friend-card-${target.user_id}`}
      className={`bg-[#0F0F0F] border-2 p-3 flex gap-3 transition ${
        fed ? "border-zinc-900 opacity-70"
            : wish ? "border-[#F472B6] shadow-[0_0_0_1px_rgba(244,114,182,0.4)_inset]"
                   : "border-[#F472B6]/40 hover:border-[#F472B6]"
      }`}
    >
      <div className="relative shrink-0">
        {target.picture ? (
          <img
            src={target.picture}
            alt=""
            className="w-12 h-12 rounded-full object-cover border-2 border-zinc-800"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-zinc-900 border-2 border-zinc-800 flex items-center justify-center text-lg font-bold text-neutral-500">
            {(target.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="absolute -bottom-1 -right-1 text-2xl leading-none" title={pet.tier_label}>
          {pet.icon}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-bold text-sm truncate" title={target.name}>{target.name}</div>
            {handle && (
              <div className="text-[10px] text-neutral-500 truncate">{handle}</div>
            )}
          </div>
          {fed ? (
            <span
              data-testid={`friend-fed-chip-${target.user_id}`}
              className="shrink-0 bg-zinc-900 border border-[#22C55E]/40 text-[#22C55E] text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 leading-none"
            >💕 Sent</span>
          ) : null}
        </div>
        {/* 💌 Their wish (if any) — shows what they're asking for */}
        {wish && !fed && (
          <div
            className="mt-1 flex items-center gap-1 text-[10px] text-[#F472B6]"
            data-testid={`friend-wish-${target.user_id}`}
          >
            <span className="text-sm leading-none">💌</span>
            <span className="truncate">wants <span className="font-bold text-white">{wish.icon} {wish.label}</span></span>
          </div>
        )}
        <div className="text-[10px] text-neutral-500 truncate mt-0.5">
          <span className="text-neutral-400">{pet.name}</span>
          <span className="mx-1 text-neutral-700">·</span>
          <span>{pet.tier_label}</span>
        </div>
        <div className="mt-1.5 h-1.5 bg-black border border-zinc-900 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#F472B6] to-[#D4AF37] transition-all"
            style={{ width: `${xpPct}%` }}
          />
        </div>
        <div className="text-[9px] text-neutral-600 tabular-nums mt-0.5">{pet.xp} / {pet.xp_cap} XP</div>
        {/* Send buttons — quick-fulfill wish + regular picker */}
        {wish && !fed ? (
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            <button
              onClick={onQuickFulfillWish}
              disabled={sending}
              data-testid={`friend-fulfill-wish-${target.user_id}`}
              className={`py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold transition ${
                sending
                  ? "bg-[#F472B6]/60 text-white cursor-wait"
                  : "bg-[#F472B6] hover:bg-[#EC4899] text-black"
              }`}
              title={`Send ${wish.label}`}
            >
              {sending ? "Sending…" : `${wish.icon} Fulfill`}
            </button>
            <button
              onClick={onOpenPicker}
              disabled={sending}
              data-testid={`friend-gift-btn-${target.user_id}`}
              className="py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold bg-black border border-[#F472B6]/50 hover:border-[#F472B6] text-[#F472B6] transition"
            >
              Other Gift
            </button>
          </div>
        ) : (
          <button
            onClick={onOpenPicker}
            disabled={fed || sending}
            data-testid={`friend-gift-btn-${target.user_id}`}
            className={`mt-2 w-full py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold transition ${
              fed
                ? "bg-zinc-900 text-neutral-600 cursor-not-allowed"
                : sending
                  ? "bg-[#F472B6]/60 text-white cursor-wait"
                  : "bg-[#F472B6] hover:bg-[#EC4899] text-black"
            }`}
          >
            {fed ? "Comes Back Tomorrow PT" : sending ? "Sending…" : `💕 Send a Gift`}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * 🎁 Gift picker — four tap-friendly tiles. NO global lock (Mar 2026):
 * pick freely per friend.
 */
function GiftPicker({ target, giftCatalog, onClose, onPick, sending }) {
  const wish = target.gift_wish;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="gift-picker-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0F0F0F] border-2 border-[#F472B6] w-full max-w-md p-5"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#F472B6] font-bold">Pick a gift for</div>
            <div className="font-display text-xl mt-0.5">{target.name}</div>
            {target.handle && (
              <div className="text-[10px] text-neutral-500">@{target.handle}</div>
            )}
            {wish && (
              <div className="mt-1.5 text-[10px] text-[#F472B6]">
                💌 wants <span className="font-bold text-white">{wish.icon} {wish.label}</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="gift-picker-close"
            className="text-neutral-500 hover:text-white text-2xl leading-none"
            aria-label="Close"
          >×</button>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          {giftCatalog.map((g) => {
            const isWish = wish && wish.id === g.id;
            return (
              <button
                key={g.id}
                onClick={() => !sending && onPick(g.id)}
                disabled={sending}
                data-testid={`gift-tile-${g.id}`}
                className={`text-left p-3 border-2 transition ${
                  sending
                    ? "bg-zinc-900 border-zinc-800 opacity-40 cursor-wait"
                    : isWish
                      ? "bg-[#F472B6]/20 border-[#F472B6] hover:bg-[#F472B6]/30 ring-2 ring-[#F472B6]/40"
                      : "bg-black border-[#F472B6]/40 hover:border-[#F472B6] hover:bg-[#F472B6]/5"
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-3xl leading-none">{g.icon}</div>
                  {isWish && (
                    <span className="text-[8px] uppercase tracking-widest text-[#F472B6] font-bold">💌 wish</span>
                  )}
                </div>
                <div className="font-bold text-sm">{g.label}</div>
                <div className="text-[10px] text-neutral-500 mt-1 leading-tight">
                  {g.description}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 text-[10px] text-neutral-500 leading-relaxed">
          Once per friend per PT day · sender pays nothing · only 💕 Hearts raises their Hearts counter.
        </div>
      </div>
    </div>
  );
}

/**
 * 💌 Wish picker — the caller sets their own gift wish so friends see it.
 */
function WishPicker({ giftCatalog, currentWish, onClose, onPick, onClear, saving }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="wish-picker-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0F0F0F] border-2 border-[#F472B6] w-full max-w-md p-5"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#F472B6] font-bold">Post a wish</div>
            <div className="font-display text-xl mt-0.5">💌 What do you want?</div>
            <div className="text-[10px] text-neutral-500 mt-1">
              Friends see this on your tile and can fulfill in one tap.
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="wish-picker-close"
            className="text-neutral-500 hover:text-white text-2xl leading-none"
            aria-label="Close"
          >×</button>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          {giftCatalog.map((g) => {
            const isCurrent = currentWish && currentWish.id === g.id;
            return (
              <button
                key={g.id}
                onClick={() => !saving && onPick(g.id)}
                disabled={saving}
                data-testid={`wish-tile-${g.id}`}
                className={`text-left p-3 border-2 transition ${
                  saving
                    ? "bg-zinc-900 border-zinc-800 opacity-40 cursor-wait"
                    : isCurrent
                      ? "bg-[#F472B6]/20 border-[#F472B6] ring-2 ring-[#F472B6]/40"
                      : "bg-black border-[#F472B6]/40 hover:border-[#F472B6] hover:bg-[#F472B6]/5"
                }`}
              >
                <div className="text-3xl mb-1.5 leading-none">{g.icon}</div>
                <div className="font-bold text-sm">{g.label}</div>
                <div className="text-[10px] text-neutral-500 mt-1 leading-tight">
                  {g.description}
                </div>
              </button>
            );
          })}
        </div>

        {currentWish && (
          <button
            onClick={onClear}
            disabled={saving}
            data-testid="wish-picker-clear"
            className="mt-3 w-full py-2 text-[10px] uppercase tracking-[0.2em] font-bold bg-black border border-neutral-700 hover:border-red-500 text-neutral-400 hover:text-red-400 transition"
          >
            Clear My Wish
          </button>
        )}
      </div>
    </div>
  );
}
